// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// E-01 kit wrapper over MUI Autocomplete for a free-text field with a typeahead of rich options
// (label + secondary line) and an optional footer action under the list — the shape asp-pot's drug
// picker (public/app.js:401 drugAutocomplete) needs: the caller owns the text, filters the options
// and reacts to a pick; the list opens once `minChars` have been typed. Pages import this, never
// @mui/material/Autocomplete.
import { type ReactNode } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Paper, { type PaperProps } from '@mui/material/Paper'
import TextField from '@mui/material/TextField'

export interface AutocompleteOption { value: string; label: string; secondary?: string }

export interface AutocompleteFieldProps<T extends AutocompleteOption> {
  id?: string
  /** The field text (free text; also what the list is filtered on by the caller). */
  value: string
  onInputChange: (v: string) => void
  /** The caller's already-filtered options for the current text. */
  options: T[]
  onPick: (option: T) => void
  /** Rendered under the option list (an action row); use `onMouseDown` prevention inside is handled here. */
  footer?: ReactNode
  minChars?: number
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  error?: boolean
  loading?: boolean
  ariaLabel?: string
  onBlur?: () => void
  title?: string
}

export default function AutocompleteField<T extends AutocompleteOption>({ id, value, onInputChange, options, onPick, footer, minChars = 2, placeholder, disabled, readOnly, error, loading, ariaLabel, onBlur, title }: AutocompleteFieldProps<T>) {
  const open = !readOnly && !disabled && value.trim().length >= minChars
  const PaperWithFooter = (p: PaperProps) => (
    <Paper {...p}>
      {p.children}
      {footer && <div onMouseDown={e => e.preventDefault()} data-testid="autocomplete-footer">{footer}</div>}
    </Paper>
  )
  return (
    <Autocomplete<T, false, true, true>
      id={id} freeSolo disableClearable forcePopupIcon={false} open={open} readOnly={readOnly} disabled={disabled} loading={loading}
      options={options} filterOptions={x => x} inputValue={value} value={value}
      getOptionLabel={o => (typeof o === 'string' ? o : o.label)}
      onInputChange={(_, v, reason) => { if (reason !== 'reset') onInputChange(v) }}
      onChange={(_, v) => { if (v && typeof v !== 'string') onPick(v) }}
      PaperComponent={PaperWithFooter}
      renderOption={(props, o) => (
        <li {...props} key={o.value}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{o.label}</div>
            {o.secondary && <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{o.secondary}</div>}
          </div>
        </li>
      )}
      renderInput={p => <TextField {...p} placeholder={placeholder} error={error} title={title} fullWidth onBlur={onBlur}
        inputProps={{ ...p.inputProps, 'aria-label': ariaLabel, autoComplete: 'off' }} sx={{ '& .MuiOutlinedInput-root': { background: 'var(--color-surface)' } }} />}
    />
  )
}
